import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Heart, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";

const LOGO = "https://customer-assets.emergentagent.com/job_wanderer-network/artifacts/w8loc1if_IMG_20260409_162234_176.webp";

export default function Donate() {
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/donation").then((r) => setSettings(r.data)); }, []);

  const qrUrl = settings?.qr_data
    ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=10&data=${encodeURIComponent(settings.qr_data)}`
    : "";

  const isSpotfund = (settings?.qr_data || "").includes("spotfund");

  const copy = () => {
    navigator.clipboard.writeText(settings.qr_data);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="donate-page" className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <div>
          <Heart className="w-7 h-7 text-[var(--forest)] mb-5" strokeWidth={1.25} />
          <div className="overline mb-3">{settings?.label || "Support the movement"}</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-6">
            Fund a<br /><em className="italic text-[var(--forest)]">life saved.</em>
          </h1>
          <p className="text-[var(--ink-soft)] text-lg leading-relaxed mb-8">
            {settings?.description || "Every dollar puts us in front of one more person who needs to hear that sobriety is possible. Gas, lodging, recording gear, resources we hand out — it all comes from people like you who believe this movement matters."}
          </p>

          <div className="flex flex-wrap gap-3 mb-6">
            {settings?.qr_data && (
              <a href={settings.qr_data} target="_blank" rel="noreferrer" data-testid="donate-open-link"
                className="inline-flex items-center gap-2 bg-[var(--terracotta)] text-[var(--ink)] px-6 py-3 rounded-full text-sm font-medium hover:bg-[var(--terracotta-dark)] transition-colors">
                {isSpotfund ? "Donate on Spotfund" : "Open donation link"} <Heart className="w-4 h-4" />
              </a>
            )}
            {settings?.qr_data && (
              <button data-testid="donate-copy-link" onClick={copy}
                className="inline-flex items-center gap-2 text-sm border border-[var(--ink)] px-5 py-2.5 rounded-full hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy link"}
              </button>
            )}
            <ShareButton
              testid="donate-share-page"
              title="Support 50 States Sober"
              text="Rick & Stephanie are driving every state in the union to prove sobriety is possible. Chip in — or just pass it on."
            />
          </div>
        </div>

        <div className="flex justify-center">
          <div className="p-8 bg-[var(--bg)] border border-[var(--line)] rounded-sm shadow-sm">
            {qrUrl ? (
              <img data-testid="donate-qr-code" src={qrUrl} alt="Donation QR code" className="w-80 h-80" />
            ) : (
              <div className="w-80 h-80 bg-[var(--bg-alt)] animate-pulse" />
            )}
            <div className="text-center mt-6 font-mono-acc text-xs text-[var(--ink-soft)] tracking-widest">
              SCAN TO DONATE
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
